import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { LogOut, Mail, Calendar, Wallet, Mic, ShieldCheck, ChevronRight, Lock, Infinity as InfinityIcon, Sparkles, LayoutDashboard, Trash2, FileText, AlertTriangle } from "lucide-react";
import { formatFullDate } from "../lib/constants";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function ProfilePage() {
  const { user, license, logout, api } = useAuth();
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const tier = license?.tier || "trial";
  const tierLabel = tier === "free_legacy" ? "Acesso gratuito vitalício"
                 : tier === "paid" ? "Licença ativa"
                 : license?.promo_active ? "Teste de lançamento" : "Precisa comprar licença";
  const TIER_STYLES = {
    emerald: { card: "border-emerald-500/30", bg: "bg-emerald-500/15", text: "text-emerald-300" },
    sky: { card: "border-sky-500/30", bg: "bg-sky-500/15", text: "text-sky-300" },
    rose: { card: "border-rose-500/30", bg: "bg-rose-500/15", text: "text-rose-300" },
  };
  const key = tier === "paid" || tier === "free_legacy" ? "emerald" : license?.promo_active ? "sky" : "rose";
  const s = TIER_STYLES[key];
  const tierIcon = tier === "free_legacy" ? InfinityIcon : tier === "paid" ? ShieldCheck : license?.promo_active ? Sparkles : Lock;
  return (
    <div className="max-w-md mx-auto px-5 pt-8 pb-6 space-y-5">
      <header>
        <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-semibold">Perfil</p>
        <h1 className="font-display text-2xl font-bold text-white">Sua conta</h1>
      </header>

      <div data-testid="profile-card" className="pnc-card p-6 text-center relative overflow-hidden">
        <div className="absolute -top-20 -right-16 w-52 h-52 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="w-24 h-24 mx-auto rounded-3xl bg-gradient-to-br from-emerald-300 to-emerald-600 flex items-center justify-center shadow-2xl shadow-emerald-500/30 mb-4">
            <span className="text-slate-900 text-3xl font-display font-black">
              {(user?.name || "?").charAt(0).toUpperCase()}
            </span>
          </div>
          <h2 className="font-display text-xl font-bold text-white">{user?.name}</h2>
          <p className="text-sm text-slate-400 mt-0.5">{user?.email}</p>
        </div>
      </div>

      <div className="pnc-card p-2 space-y-1">
        <InfoRow icon={Mail} label="E-mail" value={user?.email} />
        <div className="pnc-divider" />
        <InfoRow icon={Calendar} label="Membro desde" value={user?.created_at ? formatFullDate(user.created_at) : "-"} />
      </div>

      {/* License status card */}
      <div data-testid="license-card" className={`pnc-card p-4 ${s.card}`}>
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 shrink-0 rounded-2xl ${s.bg} ${s.text} flex items-center justify-center`}>
            {React.createElement(tierIcon, { className: "w-5 h-5", strokeWidth: 2.3 })}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Sua licença</p>
            <p className="text-sm font-bold text-white">{tierLabel}</p>
            {tier === "paid" && license?.license_purchased_at && (
              <p className="text-[11px] text-slate-400 mt-0.5">Desde {formatFullDate(license.license_purchased_at)}</p>
            )}
            {tier === "trial" && license?.needs_payment && (
              <p className="text-[11px] text-slate-400 mt-0.5">Pagamento único de R$ {(license.price_brl_cents / 100).toFixed(2).replace(".", ",")}</p>
            )}
          </div>
          {tier === "trial" && license?.needs_payment && (
            <Link
              data-testid="license-upgrade-btn"
              to="/pagar"
              className="shrink-0 px-3 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-slate-900 text-xs font-bold"
            >Comprar</Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <FeatureCard
          icon={Mic}
          title="Voz sempre à mão"
          desc="Registre falando. Whisper transcreve e a IA categoriza."
        />
        <FeatureCard
          icon={ShieldCheck}
          title="Dados seguros"
          desc="Cada usuário vê apenas as próprias movimentações."
        />
        <FeatureCard
          icon={Wallet}
          title="Feito para o dia a dia"
          desc="Simples, rápido e no português do Brasil."
        />
      </div>

      <button
        data-testid="logout-btn"
        onClick={logout}
        className="w-full py-3.5 rounded-2xl bg-rose-500/15 border border-rose-500/25 text-rose-300 hover:bg-rose-500/25 font-semibold flex items-center justify-center gap-2 text-sm"
      >
        <LogOut className="w-4 h-4" /> Sair da conta
      </button>

      {user?.role === "owner" && (
        <Link
          data-testid="owner-admin-link"
          to="/admin"
          className="w-full py-3.5 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 font-semibold flex items-center justify-center gap-2 text-sm"
        >
          <LayoutDashboard className="w-4 h-4" /> Painel do proprietário
        </Link>
      )}

      {/* Privacy & LGPD */}
      <div className="pnc-card p-2 space-y-1">
        <Link
          to="/politica-de-privacidade"
          data-testid="profile-privacy-link"
          className="flex items-center gap-3 p-3 hover:bg-white/5 rounded-xl transition"
        >
          <div className="w-9 h-9 rounded-xl bg-slate-800/70 border border-white/5 flex items-center justify-center text-emerald-300">
            <FileText className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">LGPD</p>
            <p className="text-sm text-white font-medium">Política de Privacidade</p>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-600" />
        </Link>
        {user?.role !== "owner" && (
          <>
            <div className="pnc-divider" />
            <button
              type="button"
              data-testid="profile-delete-account-btn"
              onClick={() => { setConfirmText(""); setShowDelete(true); }}
              className="w-full flex items-center gap-3 p-3 hover:bg-rose-500/10 rounded-xl transition text-left"
            >
              <div className="w-9 h-9 rounded-xl bg-rose-500/15 border border-rose-500/25 flex items-center justify-center text-rose-300">
                <Trash2 className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-wider text-rose-300/80 font-semibold">Permanente</p>
                <p className="text-sm text-white font-medium">Excluir minha conta</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-600" />
            </button>
          </>
        )}
      </div>

      {showDelete && (
        <DeleteAccountDialog
          onCancel={() => setShowDelete(false)}
          onConfirm={async () => {
            setDeleting(true);
            try {
              await api.delete("/auth/me");
              toast.success("Sua conta foi excluída.");
              logout();
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Não foi possível excluir sua conta agora.");
              setDeleting(false);
            }
          }}
          deleting={deleting}
          confirmText={confirmText}
          setConfirmText={setConfirmText}
          userEmail={user?.email}
        />
      )}

      <p className="text-center text-[11px] text-slate-500">
        Põe na Conta • Sua vida financeira, na ponta da língua.
      </p>
    </div>
  );
}

function DeleteAccountDialog({ onCancel, onConfirm, deleting, confirmText, setConfirmText, userEmail }) {
  const canConfirm = confirmText.trim().toUpperCase() === "EXCLUIR";
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="delete-account-dialog">
      <div className="pnc-card w-full max-w-md p-6 space-y-4 border-rose-500/30">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-rose-500/20 border border-rose-500/30 text-rose-300 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display text-lg font-bold text-white">Excluir sua conta?</h3>
            <p className="text-xs text-slate-400">Esta ação é <strong>permanente</strong> e não pode ser desfeita.</p>
          </div>
        </div>
        <ul className="text-xs text-slate-300 space-y-1 bg-rose-500/5 border border-rose-500/15 rounded-xl p-3">
          <li>• Removeremos sua conta ({userEmail}) e <strong>todos</strong> os seus lançamentos financeiros.</li>
          <li>• Registros de pagamento, se houver, são <strong>anonimizados</strong> para cumprimento fiscal.</li>
          <li>• Nenhum dado de outros usuários é afetado.</li>
          <li>• Sua sessão será encerrada automaticamente.</li>
        </ul>
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Digite <span className="text-rose-300">EXCLUIR</span> para confirmar</label>
          <input
            data-testid="delete-confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="EXCLUIR"
            className="w-full px-3 py-2 rounded-xl bg-slate-950/60 border border-white/10 text-white placeholder:text-slate-600 outline-none focus:border-rose-500/50 uppercase tracking-wider text-sm"
          />
        </div>
        <div className="flex gap-2">
          <button
            data-testid="delete-cancel-btn"
            onClick={onCancel}
            disabled={deleting}
            className="flex-1 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-semibold"
          >Cancelar</button>
          <button
            data-testid="delete-confirm-btn"
            onClick={onConfirm}
            disabled={!canConfirm || deleting}
            className="flex-1 py-3 rounded-xl bg-rose-500 hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold"
          >{deleting ? "Excluindo…" : "Excluir conta"}</button>
        </div>
        <p className="text-[11px] text-slate-500 text-center">
          Veja o que será removido na <Link to="/politica-de-privacidade" className="text-emerald-300 underline">Política de Privacidade</Link>.
        </p>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 p-3">
      <div className="w-9 h-9 rounded-xl bg-slate-800/70 border border-white/5 flex items-center justify-center text-slate-400">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
        <p className="text-sm text-white font-medium truncate">{value}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-600" />
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desc }) {
  return (
    <div className="pnc-card p-4 flex items-center gap-3">
      <div className="w-11 h-11 shrink-0 rounded-2xl bg-gradient-to-br from-emerald-400/20 to-emerald-600/10 border border-emerald-500/20 text-emerald-300 flex items-center justify-center">
        <Icon className="w-5 h-5" strokeWidth={2.2} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-bold text-white">{title}</p>
        <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
      </div>
    </div>
  );
}
